import Head from 'next/head';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import PropTypes from 'prop-types';
import {pathOr} from 'ramda';
import React, {useEffect} from 'react';

import {SIDE_BAR} from '@/src/app/constants/howToStoryblok';
import requestStoryBlok from '@/src/app/utils/requestStoryBlok';
import {useStoryblok} from '@/src/app/utils/storyblok';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import Feedback from '@/src/howTo/containers/Feedback';
import HowTo from '@/src/howTo/containers/HowTo';
import HowToContent from '@/src/howTo/containers/HowToContent';
import MainContainer from '@/src/main/container/MainContainer';
import {Text} from '@/src/ui';

import {LANDING_PAGE_SLUG} from '../index';
import {PREVIEW_IMAGE_PATH} from '../job-portal/position/[slug]';

export default function Page({story, preview, locale, sidebarStory, host}) {
  const {asPath, defaultLocale} = useRouter();
  const {t} = useTranslation('common');
  const {helpers, HOW_T0, HOW_T0_RESULT} = useRoutePaths();
  const fullPath = `https://${host}${asPath}`;
  const imagesPath = `https://${host}/${PREVIEW_IMAGE_PATH}`;
  const prepareStory = useStoryblok(story, preview, locale);

  const translatedSlugs = story.translated_slugs.reduce(
    (previousValue, currentValue) => {
      return {
        ...previousValue,
        [currentValue.lang]: `/${currentValue.path}`,
      };
    },
    {}
  );

  useEffect(() => {
    // @ts-ignore
    helpers.addNewRoute({
      HOW_TO_ROUTE: {
        path: `/${prepareStory.full_slug}`,
        paths: {
          destination: `/${prepareStory.default_full_slug}`,
          [defaultLocale]: `/${prepareStory.default_full_slug}`,
          ...translatedSlugs,
        },
      },
    });
  }, [prepareStory.uuid]);

  const isSidebarPage = asPath.includes('howtosidebar');
  const metaDescription = pathOr(
    null,
    ['content', 'content', 'content', 0, 'content', 0, 'text'],
    prepareStory
  );

  return (
    <>
      <Head>
        <title>{prepareStory.content.title}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="title" content={prepareStory.content.title} />

        {/*Open Graph / Facebook*/}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullPath} />
        <meta property="og:title" content={prepareStory.content.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={imagesPath} />

        {/*Twitter*/}
        <meta name="twitter:site" content={host} />
        <meta name="twitter:title" content={prepareStory.content.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imagesPath} />
      </Head>
      <div>
        <MainContainer
          topOffset={MainContainer.TOP_OFFSET.OFFSET_0}
          widthContainer={MainContainer.CONTAINER_WIDTH.FULL_WIDTH}
        >
          <HowTo
            sidebarStory={sidebarStory.content}
            indexPage={HOW_T0}
            resultPage={HOW_T0_RESULT}
            title={t('wiki-how.title')}
          >
            <Feedback pageId={story.uuid}>
              {!isSidebarPage && (
                <>
                  <div>
                    <Text
                      fontSize={Text.FONT_SIZE.SIZE_32}
                      fontWeight={Text.FONT_WEIGHT.BOLD}
                      tagComponent={Text.TAG.H2}
                    >
                      {prepareStory.content.title}
                    </Text>
                  </div>
                  <br />

                  <HowToContent story={prepareStory.content.content} />
                </>
              )}
            </Feedback>
          </HowTo>
        </MainContainer>
      </div>
    </>
  );
}

export async function getServerSideProps({
  locale,
  params,
  defaultLocale,
  ...context
}) {
  let slug = params.slug ? params.slug.join('/') : LANDING_PAGE_SLUG;
  const sideBarSlug = SIDE_BAR;

  const insideStoryblok = context.query._storyblok;
  const shouldLoadDraft = context.preview || insideStoryblok;

  // load the published content outside of the preview mode
  let storyblokParams: IStorblokParams = {
    version: 'publish',
    language: locale,
  };

  if (shouldLoadDraft) {
    storyblokParams.version = 'draft';
    storyblokParams.cv = Date.now();
  }

  try {
    const [page, sideBar] = await Promise.all([
      await requestStoryBlok({
        slug: `wiki-how/${slug}`,
        query: storyblokParams,
      }),
      await requestStoryBlok({
        slug: sideBarSlug,
        query: {
          ...storyblokParams,
          resolve_links: 'story',
        },
      }),
    ]);

    const story = page?.data?.story;
    const sidebarStory = sideBar?.data?.story;

    return {
      props: {
        host: context.req.headers.host,
        locale,
        story: page.data ? page.data.story : false,
        sidebarStory: sideBar.data ? sideBar.data.story : false,
        preview: shouldLoadDraft || false,
        isDarkMode:
          story?.content?.darkTheme || sidebarStory?.content?.darkTheme,
        ...(await serverSideTranslations(locale, ['common'])),
      },
    };
  } catch (error) {
    return {
      redirect: {
        destination: locale === defaultLocale ? '/404' : `/${locale}/404`,
        locale: true,
      },
    };
  }
}

Page.propTypes = {
  story: PropTypes.object.isRequired,
  sidebarStory: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  preview: PropTypes.bool.isRequired,
};
